import React, { useMemo, useState } from "react";
import { UserPlus, X, Search, Lock, ShieldCheck } from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function GiveAccessSection({ factoryUsers, accessIds, creatorId, creatorName, onChange, readOnly }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const byId = useMemo(() => Object.fromEntries(factoryUsers.map((u) => [u.id, u])), [factoryUsers]);

  const granted = accessIds.filter((id) => id !== creatorId);

  const results = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return [];
    return factoryUsers
      .filter((u) => u.id !== creatorId && !accessIds.includes(u.id))
      .filter((u) => u.full_name.toLowerCase().includes(t) || u.email.toLowerCase().includes(t))
      .slice(0, 6);
  }, [search, factoryUsers, accessIds, creatorId]);

  const add = (u) => {
    onChange([...accessIds.filter((id) => id !== u.id), u.id]);
    setSearch("");
    setOpen(false);
  };
  const remove = (id) => onChange(accessIds.filter((x) => x !== id));

  return (
    <section data-testid="give-access-section" className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-blue-600" />
        <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-700">Give Access</h3>
      </div>
      <p className="mt-1 text-base text-slate-500">People with access can view and edit this order. Others in the factory cannot open it.</p>

      {!readOnly && (
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="access-search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search people by name or email…"
            className="h-12 w-full rounded-lg border border-slate-200 bg-white pl-11 pr-4 text-base text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          {open && results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
              {results.map((u) => (
                <button
                  key={u.id}
                  data-testid={`access-result-${u.id}`}
                  type="button"
                  onClick={() => add(u)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-slate-800">{u.full_name}</p>
                    <p className="truncate text-sm text-slate-500">{u.email} · {ROLE_LABELS[u.role] || u.role}</p>
                  </div>
                  <UserPlus className="h-5 w-5 shrink-0 text-blue-600" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 space-y-2">
        {/* Creator (always has access) */}
        <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-slate-800">{creatorName || "Order creator"}</p>
            <p className="text-sm text-slate-500">Creator · full access</p>
          </div>
          <Lock className="h-4 w-4 text-slate-400" />
        </div>

        {granted.map((id) => {
          const u = byId[id];
          return (
            <div key={id} data-testid={`access-granted-${id}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-slate-800">{u ? u.full_name : "User"}</p>
                <p className="truncate text-sm text-slate-500">{u ? `${u.email} · ${ROLE_LABELS[u.role] || u.role}` : id}</p>
              </div>
              {!readOnly && (
                <button data-testid={`access-remove-${id}`} type="button" onClick={() => remove(id)}
                  className={cn("flex h-9 w-9 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50")} title="Remove access">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-sm text-slate-400">Your assigned Manager automatically has access to orders you create.</p>
    </section>
  );
}
